// import OnBoardingSkeleton from '@/components/on-boarding/Skeleton';
import Button from '@/components/Button';
import Container from '@/components/Container';
import SubContainer from '@/components/Container/SubContainer';
import { useAdminStore } from '../store/useAdmin.store';
import { useEffect, useState } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useAuthentication } from '@/hooks/useAuthentication';
import Tooltip from '@/components/Tooltip';
import { downloadPDF, inviteStaff, importStaff } from '@/services/global/admin.service';
import { useToast } from '@/components/ui/use-toast';
import { AdminTabs, AdminTabsEnum } from '../components/tabs';
import ExcelUploader, { RowData } from '../components/spreadsheet';
import { UserPreviewDialog } from '../components/UserPreviewDialog';

export const SchoolManagement = () => {
  const { page, onInit } = useAdminStore();
  const [template, setTemplate] = useState('');
  const [loadingPDF, setLoadingPDF] = useState(false);
  const [loadingInvite, setLoadingInvite] = useState(false);
  const [loadingImport, setLoadingImport] = useState(false);
  const [loadingSendInvitation, setLoadingSendInvitation] = useState(false);
  const { authentication } = useAuthentication();
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [usersPreview, setUsersPreview] = useState<RowData[]>([]);
  const [isPreviewDialogOpen, setIsPreviewDialogOpen] = useState(false);
  const { toast } = useToast();

  const forename = authentication.user.forename || 'Guest';
  const schoolName = authentication.user.school.name;
  const uniqueCode = authentication.user.school.code;

  useEffect(() => {
    if (!page) onInit();
  }, [onInit, page]);

  useEffect(() => {
    const template = page?.schoolManagement?.template
      ? page?.schoolManagement?.template.replace(/\${schoolName}/g, schoolName).replace(/\${uniqueCode}/g, uniqueCode)
      : '';
    setTemplate(template || '');
  }, [page, schoolName, uniqueCode]);

  const onShowInvitationPreview = async () => {
    setLoadingInvite(true);
    const { data, error } = await inviteStaff(authentication.user.school.id, true);
    if (error) {
      toast({
        title: page?.schoolManagement?.toastStaffInvitedTitle || '',
        description: error.message,
        variant: 'destructive',
      });
      setLoadingInvite(false);
      return;
    }

    setUsersPreview(data.importedUsers);
    setIsPreviewDialogOpen(true);
    setLoadingInvite(false);
  };

  const onSendInvitationEmail = async () => {
    setLoadingSendInvitation(true);
    const { data, error } = await inviteStaff(authentication.user.school.id, false);
    if (error) {
      toast({
        title: page?.schoolManagement?.toastStaffInvitedTitle || '',
        description: error.message,
        variant: 'destructive',
      });
      setLoadingSendInvitation(false);
      return;
    }

    toast({
      title: page?.schoolManagement?.toastStaffInvitedTitle || '',
      description: `${data.size} ${page?.schoolManagement?.toastStaffInvitedDescription || ''}`,
    });
    setIsPreviewDialogOpen(false);
    setLoadingSendInvitation(false);
  };

  const onImportStaff = async (users?: RowData[]) => {
    setLoadingImport(true);
    const { data, error } = await importStaff(authentication.user.school.id, users);

    if (error) {
      toast({
        title: page?.schoolManagement?.toastStaffImportedTitle || '',
        description: error.message,
        variant: 'destructive',
      });
      setLoadingImport(false);
      return;
    }

    toast({
      title: page?.schoolManagement?.toastStaffImportedTitle || '',
      description: `${data.size} ${page?.schoolManagement?.toastStaffImportedDescription || ''}`,
    });
    setLoadingImport(false);
  };

  const downloadQRCodePDF = async () => {
    setLoadingPDF(true);
    const { data, error } = await downloadPDF();

    if (error) {
      toast({
        title: page?.schoolManagement?.toastErrorDownloadingPDFTitle || '',
        description: error.message,
        variant: 'destructive',
      });
      setLoadingPDF(false);
      return;
    }

    window.open(data.url, '_blank');

    toast({
      title: page?.schoolManagement?.toastPDFDownloadedTitle || '',
      description: page?.schoolManagement?.toastPDFDownloadedDescription || '',
    });
    setLoadingPDF(false);
  };

  return (
    <Container>
      <SubContainer className="mt-sh-60 flex flex-col items-start justify-start px-4 lg:col-start-3 lg:col-end-11">
        <AdminTabs activeTab={AdminTabsEnum.SchoolManagement} />
        <div className="relative mt-sh-30 flex w-full flex-col items-start justify-start gap-sh-15">
          <h1 className=" text-sh-md font-bold">
            {page?.schoolManagement.welcomeTitle} {' ' + forename}
          </h1>
          <p className="  text-sh-xs">{page?.schoolManagement.welcomeDescription}</p>

          <p className=" my-5 rounded-2xl bg-[#EBF2FC] px-3 py-5 text-sh-md">
            {page?.schoolManagement.yourAccessCode} <strong>{uniqueCode}</strong>
          </p>

          <Button
            onClick={() => {
              navigator.clipboard.writeText(uniqueCode);
              setTooltipVisible(true);
              setTimeout(() => setTooltipVisible(false), 2000);
            }}>
            {page?.schoolManagement.btnCopyCodeTxt}
          </Button>
          <Tooltip visible={tooltipVisible} message={page?.schoolManagement?.tooltipCopiedMessage || ''} />
        </div>

        <div className="mt-sh-30 flex w-full flex-col items-start justify-start gap-sh-15">
          <h1 className=" text-sh-md font-bold">{page?.schoolManagement.shareCodeTitle}</h1>
          <p className=" text-sh-xs">{page?.schoolManagement.sendEmailSubTitle}</p>
          <div className="flex items-center justify-start">
            <div className="border-primary-500 pr-4">
              <Button loading={loadingInvite} onClick={() => onShowInvitationPreview()}>
                {page?.schoolManagement.btnInviteStaffTxt}
              </Button>
            </div>
            <div className="border-primary-500 pr-4">
              <Button loading={loadingImport} onClick={() => onImportStaff()}>
                {page?.schoolManagement.btnImportStaffWondeTxt}
              </Button>
            </div>

            <p className="text-sh-xs">{page?.schoolManagement.orText}</p>
            <ExcelUploader onImportStaff={onImportStaff} />
          </div>
        </div>

        <div className="mt-sh-30 flex w-full flex-col items-start justify-start gap-sh-15">
          <h1 className=" text-sh-md font-bold">{page?.schoolManagement.downloadQrCode}</h1>
          <p className=" text-sh-xs">{page?.schoolManagement.downloadQrCodeDescription}</p>
          <Button
            loading={loadingPDF}
            onClick={() => {
              downloadQRCodePDF();
            }}>
            {page?.schoolManagement.btnDownloadPdf}
          </Button>
        </div>

        <div className="mt-sh-30 flex w-full flex-col items-start justify-start gap-sh-15">
          <h1 className=" text-sh-md font-bold">{page?.schoolManagement.copyPasteTitle}</h1>
          <p className=" text-sh-xs">{page?.schoolManagement.copyTxtSubTItle}</p>
          <ReactQuill theme="snow" value={template} onChange={setTemplate} />
        </div>

        <UserPreviewDialog
          isOpen={isPreviewDialogOpen}
          onClose={() => setIsPreviewDialogOpen(false)}
          users={usersPreview}
          onConfirm={onSendInvitationEmail}
          loading={loadingSendInvitation}
        />
      </SubContainer>
    </Container>
  );
};
